import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, DollarSign, Clock, Truck, TrendingUp, BarChart3 } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import Card from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Analytics {
  period: string;
  total_shipments: number;
  total_cost: number;
  avg_cost: number;
  carrier_breakdown: Array<{ carrier: string; count: number; total_cost: number }>;
  avg_hours_to_label: number;
  avg_hours_to_ship: number;
  avg_days_to_deliver: number;
  queue_count: number;
}

export default function FulfillmentDashboard() {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [period, setPeriod] = useState('30d');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      setIsLoading(true);
      try {
        const data = await api.get<Analytics>(`/admin/fulfillment/analytics?period=${period}`);
        setAnalytics(data);
      } catch { /* ignore */ }
      finally { setIsLoading(false); }
    }
    fetch();
  }, [period]);

  if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  if (!analytics) return <Card padding="lg"><p className="text-center text-gray-400">No analytics data available.</p></Card>;

  const statCards = [
    { label: 'Fulfillment Queue', value: analytics.queue_count.toString(), icon: Package, color: 'text-blue-600', bg: 'bg-blue-50',
      link: '/admin/fulfillment?tab=queue' },
    { label: 'Total Shipments', value: analytics.total_shipments.toString(), icon: Truck, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Total Shipping Cost', value: formatCurrency(analytics.total_cost), icon: DollarSign, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Avg Cost/Label', value: formatCurrency(analytics.avg_cost), icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Avg Time to Ship', value: `${analytics.avg_hours_to_ship.toFixed(1)}h`, icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Avg Delivery Time', value: `${analytics.avg_days_to_deliver.toFixed(1)}d`, icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Shipping Analytics</h1>
        <Select value={period} onChange={e => setPeriod(e.target.value)}
          options={[
            { label: 'Last 7 days', value: '7d' },
            { label: 'Last 30 days', value: '30d' },
            { label: 'Last 90 days', value: '90d' },
          ]} />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map(stat => (
          <Card key={stat.label}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${stat.bg}`}>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                {stat.link ? (
                  <Link to={stat.link} className="text-2xl font-bold text-gray-900 hover:text-blue-600">{stat.value}</Link>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Carrier Breakdown */}
      <Card title="Carrier Breakdown" padding="lg">
        {analytics.carrier_breakdown.length === 0 ? (
          <p className="text-gray-400 text-sm">No shipments in this period.</p>
        ) : (
          <div className="space-y-3">
            {analytics.carrier_breakdown.map(cb => {
              const pct = analytics.total_shipments > 0
                ? (cb.count / analytics.total_shipments * 100).toFixed(0) : '0';
              return (
                <div key={cb.carrier}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 uppercase">{cb.carrier}</span>
                    <span className="text-sm text-gray-500">
                      {cb.count} shipments - {formatCurrency(cb.total_cost)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Quick Links */}
      <div className="flex gap-3 flex-wrap">
        <Link to="/admin/fulfillment?tab=queue">
          <Card>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Package className="h-4 w-4" /> Fulfillment Queue
            </div>
          </Card>
        </Link>
        <Link to="/admin/fulfillment?tab=shipments">
          <Card>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Truck className="h-4 w-4" /> Shipment History
            </div>
          </Card>
        </Link>
        <Link to="/admin/fulfillment-rules">
          <Card>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <TrendingUp className="h-4 w-4" /> Automation Rules
            </div>
          </Card>
        </Link>
      </div>
    </div>
  );
}
